import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SERVICES } from "../lib/data/services";
import { Reveal, RevealLines } from "../components/motion/Reveal";
import EditorialMarquee from "../components/site/EditorialMarquee";
import Seo from "../components/site/Seo";
import { canonicalFor } from "../lib/data/site";

export default function Services() {
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="pt-24">
      <Seo
        title="Security Services in British Columbia | Guards, Patrols & Marine"
        description="Compare guard, mobile patrol, mining, marine and event security across British Columbia. Request a site-specific plan."
        keywords={[
          "security services British Columbia",
          "security guards BC",
          "mobile patrol BC",
          "marine security British Columbia",
          "mining security BC",
          "event security Vancouver",
          "licensed security company BC",
          ...SERVICES.flatMap((s) => s.keywords || []).slice(0, 20),
        ]}
        canonical={canonicalFor("/services")}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Security Services in British Columbia",
          description: "Compare guard, mobile patrol, mining, marine and event security across British Columbia.",
          url: canonicalFor("/services"),
          hasPart: SERVICES.map((s) => ({
            "@type": "Service",
            name: s.name,
            url: canonicalFor(`/services/${s.slug}`),
            description: s.tagline,
          })),
        }}
      />

      {/* Hero — one descriptive, crawlable H1 */}
      <section className="pt-16 md:pt-24 pb-16 bg-[#F4F4F0]" data-testid="services-hero">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
            Directory · Services
          </div>
          <RevealLines
            as="h1"
            lines={["Security services", "across British Columbia."]}
            className="mt-6 font-display text-6xl md:text-8xl lg:text-9xl tracking-tighter leading-[0.9] text-[#060913]"
          />
          <p className="mt-10 max-w-2xl text-lg md:text-xl text-[#5C5F66] leading-relaxed">
            Compare guard, mobile patrol, mining, marine and event security
            across British Columbia. From remote mining camps to downtown
            Vancouver concierge desks — every discipline, delivered under one
            provincial contract.
          </p>
        </div>
      </section>

      <EditorialMarquee items={["MOBILE PATROL", "MINING", "MARINE", "CANNABIS", "CORPORATE", "EVENTS", "RETAIL", "RESIDENTIAL", "CONSTRUCTION"]} />

      {/* Editorial list */}
      <section className="relative bg-[#F4F4F0]" data-testid="services-list">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative">
          <div className="border-t border-black/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.03}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`services-row-${s.slug}`}
                  onMouseEnter={() => setHovered(s)}
                  onMouseLeave={() => setHovered(null)}
                  className="relative grid grid-cols-12 gap-4 py-10 md:py-14 border-b border-black/10 group items-baseline"
                >
                  <div className="col-span-2 md:col-span-1 font-mono text-[11px] tracking-[0.24em] text-black/50">
                    {s.number}
                  </div>
                  <h2 className="col-span-10 md:col-span-6 font-display text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#060913] group-hover:text-[#D92323] transition-colors">
                    {s.name}
                  </h2>
                  <div className="col-span-12 md:col-span-4 text-[#5C5F66] text-base leading-relaxed">
                    {s.tagline}
                  </div>
                  <div className="col-span-12 md:col-span-1 text-right font-mono text-[11px] tracking-[0.22em] uppercase text-black/50 group-hover:text-[#D92323]">
                    →
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>

        {hovered && (
          <div className="hidden md:block fixed pointer-events-none top-1/2 right-10 -translate-y-1/2 w-64 h-80 z-40 overflow-hidden shadow-2xl">
            <img
              src={hovered.image}
              alt={hovered.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-2 left-2 font-mono text-[10px] tracking-[0.22em] uppercase text-white/90 mix-blend-difference">
              {hovered.number} · {hovered.name}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
